import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Building2, CheckCircle2, ChevronRight, FolderOpen, Network, Settings, Shield } from "lucide-react";
import Menu from "../components/menu";
import ProfileInfo from "../components/profile-info";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import ContentLoader from "../../components/ContentLoader";
import { useAuth } from "../../auth/AuthContext";
import { useMenu } from "../../Common/MenuContext";
import {
  loadSuperAdminDashboardData,
  mergeSuperAdminDashboardPendingCounts,
  projectManagementLink,
  type SuperAdminDashboardData,
} from "../../utils/superAdminDashboardUtils";
import "../../assets/css/dashboard.css";
import "../../assets/css/super-admin-dashboard.css";
import { FRAMEWORK_STUDIO_LABELS } from "../../utils/frameworkStudioLabels";

const FRAMEWORK_WORKFLOW = [
  {
    step: 1,
    label: FRAMEWORK_STUDIO_LABELS.sourceDocuments,
    desc: "Upload and manage source documents for framework generation",
    path: "/sa/FrameworkManagement",
    countKey: "sourceFrameworks" as const,
  },
  {
    step: 2,
    label: FRAMEWORK_STUDIO_LABELS.capabilityCatalog,
    desc: "Define capabilities and sub-capabilities",
    path: "/sa/capability_management",
    countKey: "capabilities" as const,
  },
  {
    step: 3,
    label: FRAMEWORK_STUDIO_LABELS.assessmentFrameworks,
    desc: "Create and manage Traxccel assessment frameworks",
    path: "/sa/TraxccelFrameworks",
    countKey: "traxccelFrameworks" as const,
  },
  {
    step: 4,
    label: FRAMEWORK_STUDIO_LABELS.assessmentAssignments,
    desc: "Create assessments from assessment frameworks — edit questions, then publish to a project",
    path: "/sa/AssessmentBlueprints",
    countKey: "assessmentBlueprints" as const,
  },
];

const QUICK_ACTIONS = [
  {
    label: "Manage companies",
    path: "/sa/company_management",
    icon: <Building2 aria-hidden />,
    group: "Clients",
  },
  {
    label: "Manage projects",
    path: "/sa/project_management",
    icon: <FolderOpen aria-hidden />,
    group: "Clients",
  },
  {
    label: "Capability requests",
    path: "/sa/capability_approval",
    icon: <CheckCircle2 aria-hidden />,
    group: "Approvals",
  },
  {
    label: "Project setup requests",
    path: "/sa/project_setup_approval",
    icon: <Network aria-hidden />,
    group: "Approvals",
  },
  {
    label: "Role permissions",
    path: "/sa/rights_management",
    icon: <Shield aria-hidden />,
    group: "Security",
  },
  {
    label: "Settings",
    path: "/sa/settings",
    icon: <Settings aria-hidden />,
    group: "Platform",
  },
];

const TOP_PROJECTS_VISIBLE_ROWS = 5;

const formatNum = (value: number | null | undefined) => {
  if (value == null) return "—";
  return value.toLocaleString();
};

const Dashboard = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    hasPendingCapabilityApprovals,
    hasPendingProjectSetup,
    pendingCapabilityCount,
    pendingProjectSetupCount,
  } = useMenu();
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SuperAdminDashboardData | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const result = await loadSuperAdminDashboardData(
          pendingCapabilityCount ?? 0,
          pendingProjectSetupCount ?? 0
        );
        if (!cancelled) setData(result);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const dashboardData = useMemo(() => {
    if (!data) return null;
    return mergeSuperAdminDashboardPendingCounts(
      data,
      pendingCapabilityCount ?? 0,
      pendingProjectSetupCount ?? 0
    );
  }, [data, pendingCapabilityCount, pendingProjectSetupCount]);

  const viewData = dashboardData ?? data;

  const pendingTotal =
    (pendingCapabilityCount ?? 0) + (pendingProjectSetupCount ?? 0);
  const hasPendingApprovals =
    Boolean(hasPendingCapabilityApprovals) || Boolean(hasPendingProjectSetup);

  const avgTeamSize = useMemo(() => {
    if (!viewData?.topProjects?.length) return 0;
    const sum = viewData.topProjects.reduce((acc, p) => acc + p.memberTotal, 0);
    return Math.round((sum / viewData.topProjects.length) * 10) / 10;
  }, [viewData]);

  const topProjects = useMemo(
    () => (viewData?.topProjects ?? []).slice(0, TOP_PROJECTS_VISIBLE_ROWS),
    [viewData?.topProjects]
  );

  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
    []
  );

  const firstName = useMemo(() => {
    const raw = user?.userName?.trim();
    if (!raw) return "Super Admin";
    return raw.split(/\s+/)[0];
  }, [user?.userName]);

  const dashReady = Boolean(viewData) && !loading;

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={(item) => {
            setSelectedItem(item);
            return item;
          }}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <div className="dash-frame-parent">
        <div className="dash-frame-group">
          <div className="dash-dashboard-wrapper">
            <div className="dash-dashboard app-heading-accent text-[var(--app-heading)]">Super Admin Dashboard</div>
          </div>
          <ProfileInfo />
        </div>

        <div className="dash-frame-wrapper">
          <div className="dash-frame-div">
            <div className={`sa-dash${dashReady ? " sa-dash--ready" : ""}`}>
              <header
                className="sa-dash__hero app-welcome-card-dark sa-dash__reveal"
                style={{ "--sa-delay": 0 } as React.CSSProperties}
              >
                <div className="app-welcome-card-glow sa-dash__hero-glow" aria-hidden />
                <div className="sa-dash__hero-inner">
                  <div className="sa-dash__hero-copy">
                    <p className="sa-dash__eyebrow">Platform command center</p>
                    <h1 className="sa-dash__title">
                      Good to see you, {firstName}
                    </h1>
                    <p className="sa-dash__subtitle">
                      Your live platform pulse: users, projects, approvals, and
                      the framework pipeline in one place.
                    </p>
                    {hasPendingApprovals && (
                      <Link
                        to={
                          hasPendingCapabilityApprovals
                            ? "/sa/capability_approval"
                            : "/sa/project_setup_approval"
                        }
                        className="sa-dash__hero-alert"
                      >
                        <AlertTriangle aria-hidden />
                        <span>
                          {formatNum(pendingTotal)} approval
                          {pendingTotal !== 1 ? "s" : ""} waiting for review
                        </span>
                        <ChevronRight aria-hidden />
                      </Link>
                    )}
                  </div>

                  <div className="sa-dash__hero-stats">
                    <p className="sa-dash__hero-date">{todayLabel}</p>
                    <div className="sa-dash__hero-stat-grid">
                      <Link
                        to="/sa/user_management"
                        className="sa-dash__hero-stat"
                        aria-label={`${formatNum(viewData?.totalUsers)} total users — open user management`}
                      >
                        <span className="sa-dash__hero-stat-value">
                          {formatNum(viewData?.totalUsers)}
                        </span>
                        <span className="sa-dash__hero-stat-label">
                          Total users
                        </span>
                      </Link>
                      <Link
                        to="/sa/company_management"
                        className="sa-dash__hero-stat"
                        aria-label={`${formatNum(viewData?.companies)} companies — open company management`}
                      >
                        <span className="sa-dash__hero-stat-value">
                          {formatNum(viewData?.companies)}
                        </span>
                        <span className="sa-dash__hero-stat-label">
                          Companies
                        </span>
                      </Link>
                      <Link
                        to="/sa/project_management"
                        className="sa-dash__hero-stat"
                        aria-label={`${formatNum(viewData?.projects)} projects — open project management`}
                      >
                        <span className="sa-dash__hero-stat-value">
                          {formatNum(viewData?.projects)}
                        </span>
                        <span className="sa-dash__hero-stat-label">
                          Projects
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              </header>

              {loading && !viewData ? (
                <ContentLoader compact label="Loading platform overview…" />
              ) : (
                <>
                  <section
                    className="sa-dash__section sa-dash__reveal"
                    aria-label="Approvals and ecosystem"
                    style={{ "--sa-delay": 60 } as React.CSSProperties}
                  >
                    <div className="sa-dash__spotlight-grid sa-dash__spotlight-grid--three">
                      <Link
                        to="/sa/capability_approval"
                        className={`sa-dash__spotlight${
                          hasPendingCapabilityApprovals
                            ? " sa-dash__spotlight--alert"
                            : " sa-dash__spotlight--calm"
                        }`}
                      >
                        <span className="sa-dash__spotlight-icon">
                          {hasPendingCapabilityApprovals ? (
                            <AlertTriangle aria-hidden />
                          ) : (
                            <CheckCircle2 aria-hidden />
                          )}
                        </span>
                        <span className="sa-dash__spotlight-body">
                          <span className="sa-dash__spotlight-label">
                            Capability requests
                          </span>
                          <span className="sa-dash__spotlight-value">
                            {formatNum(pendingCapabilityCount)}
                          </span>
                          <span className="sa-dash__spotlight-hint">
                            {hasPendingCapabilityApprovals
                              ? "Awaiting your review"
                              : "Queue is clear"}
                          </span>
                        </span>
                        <ChevronRight
                          className="sa-dash__spotlight-arrow"
                          aria-hidden
                        />
                      </Link>

                      <Link
                        to="/sa/project_setup_approval"
                        className={`sa-dash__spotlight${
                          hasPendingProjectSetup
                            ? " sa-dash__spotlight--alert"
                            : " sa-dash__spotlight--calm"
                        }`}
                      >
                        <span className="sa-dash__spotlight-icon">
                          {hasPendingProjectSetup ? (
                            <AlertTriangle aria-hidden />
                          ) : (
                            <CheckCircle2 aria-hidden />
                          )}
                        </span>
                        <span className="sa-dash__spotlight-body">
                          <span className="sa-dash__spotlight-label">
                            Project setup requests
                          </span>
                          <span className="sa-dash__spotlight-value">
                            {formatNum(pendingProjectSetupCount)}
                          </span>
                          <span className="sa-dash__spotlight-hint">
                            {hasPendingProjectSetup
                              ? "New client setups pending"
                              : "Queue is clear"}
                          </span>
                        </span>
                        <ChevronRight
                          className="sa-dash__spotlight-arrow"
                          aria-hidden
                        />
                      </Link>

                      <Link
                        to="/sa/project_management"
                        className="sa-dash__spotlight sa-dash__spotlight--primary"
                      >
                        <span className="sa-dash__spotlight-icon sa-dash__spotlight-icon--muted">
                          <FolderOpen aria-hidden />
                        </span>
                        <span className="sa-dash__spotlight-body">
                          <span className="sa-dash__spotlight-label">
                            Active ecosystem
                          </span>
                          <span className="sa-dash__spotlight-value sa-dash__spotlight-value--split">
                            <span>{formatNum(viewData?.companies)}</span>
                            <span className="sa-dash__spotlight-divider">
                              /
                            </span>
                            <span>{formatNum(viewData?.projects)}</span>
                          </span>
                          <span className="sa-dash__spotlight-hint">
                            Companies · Projects
                          </span>
                        </span>
                        <ChevronRight
                          className="sa-dash__spotlight-arrow"
                          aria-hidden
                        />
                      </Link>
                    </div>
                  </section>

                  <div
                    className="sa-dash__layout sa-dash__layout--pair sa-dash__reveal"
                    style={{ "--sa-delay": 120 } as React.CSSProperties}
                  >
                    <section
                      className="sa-dash__section"
                      aria-label="Framework workflow"
                    >
                      <div className="sa-dash__data-card app-panel">
                        <div className="sa-dash__section-head sa-dash__section-head--in-card">
                          <div>
                            <h2 className="sa-dash__section-title">
                              Framework studio
                            </h2>
                            <p className="sa-dash__section-sub">
                              Source to publish pipeline
                            </p>
                          </div>
                          <Link
                            to="/sa/FrameworkManagement"
                            className="sa-dash__section-link"
                          >
                            Open
                            <ChevronRight aria-hidden />
                          </Link>
                        </div>
                        <ul className="sa-dash__data-list sa-dash__data-list--workflow">
                          {FRAMEWORK_WORKFLOW.map((item) => (
                            <li key={item.path}>
                              <Link
                                to={item.path}
                                className="sa-dash__data-row"
                              >
                                <span className="sa-dash__data-index">
                                  {item.step}
                                </span>
                                <span className="sa-dash__data-cell">
                                  <span className="sa-dash__data-title">
                                    {item.label}
                                  </span>
                                  <span className="sa-dash__data-meta">
                                    {item.desc}
                                  </span>
                                </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                        <div className="sa-dash__catalog-strip">
                          {FRAMEWORK_WORKFLOW.map((item) => (
                            <Link
                              key={`strip-${item.path}`}
                              to={item.path}
                              className="sa-dash__catalog-pill"
                              aria-label={`${formatNum(viewData?.frameworkCatalog?.[item.countKey])} ${item.label}`}
                            >
                              <span className="sa-dash__catalog-pill-value">
                                {formatNum(
                                  viewData?.frameworkCatalog?.[item.countKey]
                                )}
                              </span>
                              <span className="sa-dash__catalog-pill-label">
                                {item.label}
                              </span>
                            </Link>
                          ))}
                        </div>
                        <Link
                          to="/sa/FrameworkManagement"
                          className="sa-dash__data-card-foot"
                        >
                          Open framework studio
                          <ChevronRight aria-hidden />
                        </Link>
                      </div>
                    </section>

                    <section
                      className="sa-dash__section"
                      aria-label="Top projects by team size"
                    >
                      <div className="sa-dash__data-card sa-dash__data-card--projects app-panel">
                        <div className="sa-dash__section-head sa-dash__section-head--in-card">
                          <div>
                            <h2 className="sa-dash__section-title">
                              Top projects
                            </h2>
                            <p className="sa-dash__section-sub">
                              {avgTeamSize > 0
                                ? `Avg. ${avgTeamSize} members`
                                : "By team size"}
                            </p>
                          </div>
                          <Link
                            to="/sa/project_management"
                            className="sa-dash__section-link"
                          >
                            View all
                            <ChevronRight aria-hidden />
                          </Link>
                        </div>
                        <div className="sa-dash__projects-table-wrap">
                          <table className="sa-dash__data-table sa-dash__data-table--projects">
                            <thead>
                              <tr>
                                <th className="sa-dash__col-index">#</th>
                                <th>Project</th>
                                <th className="sa-dash__col-num">Members</th>
                              </tr>
                            </thead>
                            <tbody>
                              {topProjects.length === 0 ? (
                                <tr className="sa-dash__data-table-row sa-dash__data-table-row--empty">
                                  <td colSpan={3}>
                                    <span className="sa-dash__table-empty-msg">
                                      No project membership data yet.
                                    </span>
                                  </td>
                                </tr>
                              ) : (
                                topProjects.map((project, index) => (
                                  <tr
                                    key={project.projectId}
                                    className="sa-dash__data-table-row"
                                    onClick={() => {
                                      navigate(
                                        projectManagementLink(project.companyId)
                                      );
                                    }}
                                    onKeyDown={(event) => {
                                      if (
                                        event.key === "Enter" ||
                                        event.key === " "
                                      ) {
                                        event.preventDefault();
                                        navigate(
                                          projectManagementLink(
                                            project.companyId
                                          )
                                        );
                                      }
                                    }}
                                    tabIndex={0}
                                    role="link"
                                    aria-label={`Open ${project.projectName} in project management`}
                                  >
                                    <td className="sa-dash__col-index">
                                      {index + 1}
                                    </td>
                                    <td>
                                      <span className="sa-dash__data-title">
                                        {project.projectName}
                                      </span>
                                      <span className="sa-dash__data-meta">
                                        {project.companyName}
                                      </span>
                                    </td>
                                    <td className="sa-dash__col-num">
                                      {formatNum(project.memberTotal)}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                        <Link
                          to="/sa/project_management"
                          className="sa-dash__data-card-foot"
                        >
                          Browse all projects
                          <ChevronRight aria-hidden />
                        </Link>
                      </div>
                    </section>
                  </div>

                  <section
                    className="sa-dash__section sa-dash__section--attention sa-dash__reveal"
                    aria-label="Needs attention"
                    style={{ "--sa-delay": 160 } as React.CSSProperties}
                  >
                    <div className="sa-dash__data-card app-panel">
                      <div className="sa-dash__section-head sa-dash__section-head--in-card">
                        <div>
                          <h2 className="sa-dash__section-title">
                            Needs attention
                          </h2>
                          <p className="sa-dash__section-sub">
                            Approvals, onboarding gaps, and team setup issues
                          </p>
                        </div>
                      </div>
                      {!viewData?.attentionItems?.length ? (
                        <p className="sa-dash__empty sa-dash__empty--compact">
                          Nothing flagged right now — platform looks healthy.
                        </p>
                      ) : (
                        <ul className="sa-dash__data-list">
                          {viewData.attentionItems.map((item) => (
                            <li key={item.id}>
                              <Link to={item.path} className="sa-dash__data-row">
                                <span
                                  className={`sa-dash__attention-dot sa-dash__attention-dot--${item.kind}`}
                                  aria-hidden
                                />
                                <span className="sa-dash__data-cell">
                                  <span className="sa-dash__data-title">
                                    {item.title}
                                  </span>
                                  <span className="sa-dash__data-meta">
                                    {item.meta}
                                  </span>
                                </span>
                                <ChevronRight
                                  className="sa-dash__attention-arrow"
                                  aria-hidden
                                />
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </section>

                  <section
                    className="sa-dash__section sa-dash__section--actions sa-dash__reveal"
                    aria-label="Quick actions"
                    style={{ "--sa-delay": 200 } as React.CSSProperties}
                  >
                    <div className="sa-dash__data-card sa-dash__data-card--actions app-panel">
                      <div className="sa-dash__section-head sa-dash__section-head--in-card sa-dash__section-head--center">
                        <h2 className="sa-dash__section-title">Quick actions</h2>
                      </div>
                      <div className="sa-dash__actions sa-dash__actions--center">
                        {QUICK_ACTIONS.map((action) => (
                        <Link
                          key={action.path}
                          to={action.path}
                          className="sa-dash__action"
                        >
                          <span className="sa-dash__action-icon">
                            {action.icon}
                          </span>
                          <span className="sa-dash__action-text">
                            <span className="sa-dash__action-group">
                              {action.group}
                            </span>
                            <span className="sa-dash__action-label">
                              {action.label}
                            </span>
                          </span>
                          <ChevronRight
                            className="sa-dash__action-arrow"
                            aria-hidden
                          />
                        </Link>
                        ))}
                      </div>
                    </div>
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
};

export default Dashboard;
